import {
  type ReactElement,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import type { CompanyWorkspace } from '@nest-react/domain';
import { useTranslation } from 'react-i18next';

import { ACTIVE_REGION_NAME_I18N_KEY } from '~/app/regionLabel';
import { useAuth, useWorkspace } from '~/app/providers';
import { CompanyForm } from './CompanyForm';

export const WorkspaceSwitcherModal = ({
  onClose,
  dismissible = true,
  triggerRef,
  initialCreating = false,
  onCreated,
}: {
  onClose: () => void;
  dismissible?: boolean;
  triggerRef?: RefObject<HTMLButtonElement | null>;
  initialCreating?: boolean;
  onCreated?: (workspace: CompanyWorkspace) => void;
}): ReactElement => {
  const { t } = useTranslation('account');
  const { session } = useAuth();
  const {
    workspaceContracts,
    activeWorkspace,
    setActiveCompany,
    createCompany,
  } = useWorkspace();
  const [creating, setCreating] = useState(
    initialCreating || workspaceContracts.length === 0
  );
  const [error, setError] = useState('');
  const dialogRef = useRef<HTMLDivElement>(null);
  const headingId = 'workspace-switcher-title';
  const dismiss = useCallback(() => {
    onClose();
    window.requestAnimationFrame(() => triggerRef?.current?.focus());
  }, [onClose, triggerRef]);

  useEffect(() => {
    const dialog = dialogRef.current;
    const focusableSelector =
      'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';
    const getFocusable = (): HTMLElement[] =>
      dialog
        ? Array.from(dialog.querySelectorAll<HTMLElement>(focusableSelector))
        : [];

    (getFocusable()[0] ?? dialog)?.focus();
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && dismissible) {
        dismiss();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = getFocusable();
      if (focusable.length === 0) {
        event.preventDefault();
        dialog?.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [dismiss, dismissible]);

  return createPortal(
    <div
      className="fixed inset-0 z-[80] grid place-items-center bg-black/60 p-4 backdrop-blur-sm"
      onMouseDown={event => {
        if (dismissible && event.target === event.currentTarget) dismiss();
      }}
    >
      <div
        aria-labelledby={headingId}
        aria-modal="true"
        className="max-h-[90vh] w-full max-w-xl overscroll-contain overflow-y-auto rounded-3xl border border-border bg-elevated p-6 shadow-2xl"
        ref={dialogRef}
        role="dialog"
        tabIndex={-1}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-primary">
              {t('regionLabel', { region: t(ACTIVE_REGION_NAME_I18N_KEY) })}
            </p>
            <h2 className="mt-2 text-2xl font-semibold" id={headingId}>
              {creating ? t('createWorkspace') : t('switchWorkspace')}
            </h2>
            <p className="mt-1 text-sm text-muted">{session?.user.email}</p>
          </div>
          {dismissible ? (
            <button
              aria-label={t('close')}
              className="rounded-xl px-3 py-2 text-muted hover:bg-hover focus-visible:outline-2 focus-visible:outline-primary"
              onClick={dismiss}
              type="button"
            >
              ×
            </button>
          ) : null}
        </div>

        {creating ? (
          <div className="mt-6">
            <CompanyForm
              onSubmit={value => {
                void createCompany(value)
                  .then(workspace => {
                    onCreated?.(workspace);
                    dismiss();
                  })
                  .catch(reason =>
                    setError(
                      reason instanceof Error
                        ? reason.message
                        : t('createWorkspaceFailed')
                    )
                  );
              }}
              submitLabel={t('createWorkspace')}
            />
          </div>
        ) : (
          <div className="mt-6 grid gap-2">
            {workspaceContracts.map(workspace => (
              <button
                className={`flex min-h-16 items-center gap-3 rounded-2xl border px-4 text-left focus-visible:outline-2 focus-visible:outline-primary ${
                  activeWorkspace?.id === workspace.id
                    ? 'border-primary/50 bg-primary/10'
                    : 'border-border hover:bg-hover'
                }`}
                key={workspace.id}
                onClick={() => {
                  setError('');
                  void setActiveCompany(workspace.id)
                    .then(dismiss)
                    .catch(reason =>
                      setError(
                        reason instanceof Error
                          ? reason.message
                          : t('switchWorkspaceFailed')
                      )
                    );
                }}
                type="button"
              >
                <span className="min-w-0 flex-1">
                  <strong className="block truncate">{workspace.name}</strong>
                  <small className="text-muted">{workspace.role}</small>
                </span>
                <span
                  className="rounded-full border border-border px-2 py-1 font-mono text-[10px] text-muted"
                  translate="no"
                >
                  {t(ACTIVE_REGION_NAME_I18N_KEY)}
                </span>
              </button>
            ))}
            <button
              aria-label={t('createNewWorkspace')}
              className="mt-2 min-h-12 rounded-2xl border border-dashed border-border text-sm font-semibold text-muted hover:bg-hover hover:text-foreground focus-visible:outline-2 focus-visible:outline-primary"
              onClick={() => setCreating(true)}
              type="button"
            >
              + {t('createNewWorkspace')}
            </button>
            <Link
              className="mt-1 min-h-11 rounded-xl px-3 py-3 text-center text-sm font-medium text-primary transition-colors hover:bg-primary/10 focus-visible:outline-2 focus-visible:outline-primary"
              onClick={dismiss}
              to="/workspaces"
            >
              {t('manageWorkspaces')}
            </Link>
          </div>
        )}
        {error ? (
          <p className="mt-4 text-sm text-rose-400" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </div>,
    document.body
  );
};
